import React, { useState } from 'react';
import { Tag, Divider, Button } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import './ParameterWidget.css';

/**
 * ParameterWidget - Pure display component for parameters (path, query, body, etc.)
 * Receives pre-parsed parameter data from useOpenApiParser
 *
 * @param {string} title - Section title (e.g., "Path Parameters", "Body")
 * @param {Array} parameters - Array of parsed parameter objects
 */
const ParameterWidget = ({ title, parameters }) => {
  // Only render if there are parameters
  if (!parameters || parameters.length === 0) {
    return null;
  }

  return (
    <div className="parameter-widget">
      {/* Header with title and divider */}
      <div className="parameter-header">
        <h3 className="parameter-title">{title}</h3>
        <Divider className="parameter-divider" />
      </div>

      {/* Parameter items */}
      <div className="parameter-content">
        {parameters.map((param, index) => (
          <React.Fragment key={`${param.name}-${index}`}>
            {index > 0 && <Divider className="parameter-item-divider" />}
            <ParameterItem parameter={param} />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

/**
 * ParameterItem - Displays a single parameter with name, tags, and description
 */
const ParameterItem = ({ parameter }) => {
  const [showChildren, setShowChildren] = useState(false);
  const hasChildren = parameter.properties && Object.keys(parameter.properties).length > 0;

  return (
    <div className="parameter-item">
      {/* Row with parameter name and tags */}
      <div className="parameter-item-row">
        {/* Parameter name as plain text */}
        <span className="parameter-name">{parameter.name}</span>

        {/* Tags */}
        {parameter.type && <Tag className="parameter-tag-default">{parameter.type}</Tag>}

        {parameter.required && <Tag className="parameter-tag-required">required</Tag>}

        {parameter.default !== undefined && (
          <Tag className="parameter-tag-default">default: {String(parameter.default)}</Tag>
        )}

        {parameter.format && <Tag className="parameter-tag-default">{parameter.format}</Tag>}

        {parameter.deprecated && <Tag className="parameter-tag-deprecated">deprecated</Tag>}
      </div>

      {/* Description */}
      {parameter.description && <p className="parameter-description">{parameter.description}</p>}

      {/* Example */}
      {parameter.example !== undefined && (
        <p className="parameter-example">
          <span className="parameter-example-label">Example:</span> {String(parameter.example)}
        </p>
      )}

      {/* Enum values */}
      {parameter.enum && parameter.enum.length > 0 && (
        <p className="parameter-enum">
          <span className="parameter-enum-label">Allowed values:</span> {parameter.enum.join(', ')}
        </p>
      )}

      {/* Show child attributes button for object types with properties */}
      {hasChildren && (
        <Button
          type="text"
          className="parameter-child-button"
          icon={<RightOutlined />}
          iconPosition="end"
          onClick={() => setShowChildren(!showChildren)}
        >
          Show child attributes
        </Button>
      )}

      {/* Child properties (if expanded) */}
      {hasChildren && showChildren && (
        <div className="parameter-children">
          {Object.entries(parameter.properties).map(([childName, childSchema]) => (
            <ParameterItem
              key={childName}
              parameter={{
                name: childName,
                type: childSchema.type,
                description: childSchema.description,
                required: false,
                default: childSchema.default,
                example: childSchema.example,
                enum: childSchema.enum,
                format: childSchema.format,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ParameterWidget;
